import { useMemo, useEffect } from 'react'
import {
  getActionableWeakPoints,
  getCodexForWeakPoint,
  countWeeklyTargetedSuccesses,
  stripDetailExamples
} from '../utils/studentProgressInsights'
import { getCodexLinkForNode } from '../utils/codexHelpers'
import { isNodeMastered } from '../utils/parcoursProgressSummary'
import CodexLinkButton from './CodexLinkButton'
import ObjectiveWhyToggle from './ObjectiveWhyToggle'
import './PracticePlan.css'

const STORAGE_KEY = 'opus_lab_practice_plan_week'
const SECTION_TITLE_ID = 'practice-plan-section-title'

function getWeekKey() {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now)
  monday.setDate(diff)
  monday.setHours(0, 0, 0, 0)
  return monday.toISOString().slice(0, 10)
}

function loadWeekState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function saveWeekState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore */
  }
}

/** Instantané d'un objectif atteint, conservé jusqu'au lundi suivant. */
function snapshotObjective(obj) {
  return {
    id: obj.id,
    title: obj.title,
    description: obj.description,
    current: obj.target,
    target: obj.target,
    type: obj.type,
    done: true,
    codex: obj.codex || null,
    detailWhy: obj.detailWhy || null,
    successRateMeta: obj.successRateMeta || null,
    focus: obj.focus
      ? {
          type: obj.focus.type,
          value: obj.focus.value,
          filterTag: obj.focus.filterTag,
          practiceLabel: obj.focus.practiceLabel,
          label: obj.focus.label
        }
      : null
  }
}

function applyWeeklyLocks(liveList, stored, weekKey) {
  const locked =
    stored?.weekKey === weekKey && stored.lockedObjectives ? stored.lockedObjectives : {}
  const liveIds = new Set(liveList.map((o) => o.id))

  const merged = liveList.map((obj) => {
    const snap = locked[obj.id]
    if (snap) {
      return { ...snap, done: true, current: snap.target, locked: true }
    }
    if (obj.done) {
      return { ...snapshotObjective(obj), locked: true }
    }
    return obj
  })

  Object.entries(locked).forEach(([id, snap]) => {
    if (!liveIds.has(id)) {
      merged.push({ ...snap, done: true, current: snap.target, locked: true })
    }
  })

  const typeOrder = { node: 0, skill: 1 }
  merged.sort((a, b) => (typeOrder[a.type] ?? 2) - (typeOrder[b.type] ?? 2))
  return merged
}

function formatSkillTitle(practiceName) {
  const name = (practiceName || '').trim()
  if (!name) return 'Consolider cette compétence'
  if (/^(la |le |les |l')/i.test(name)) return `Consolider ${name}`
  return `Consolider ${name}`
}

function buildNodeObjectiveCopy(target) {
  return `${target} succès consécutifs ≥ 70 % sur ce nœud`
}

function buildSkillObjectiveCopy(focus, target) {
  const rate = Math.round(focus.successRate ?? 0)
  const detailWhy =
    focus.type === 'pattern' && focus.detail ? stripDetailExamples(focus.detail) : null
  return {
    description: `${target} réponses ciblées réussies cette semaine`,
    detailWhy,
    successRateMeta: !detailWhy && rate > 0 ? `${rate} % de réussite actuellement` : null
  }
}

function PracticePlanObjectiveCard({ obj, onGoParcours, onWorkSkill, onOpenCodex }) {
  const pct = Math.min(100, Math.round((obj.current / obj.target) * 100))
  const practiceName = obj.focus?.practiceLabel || obj.focus?.label || obj.title
  const detailId = `practice-plan-why-${obj.id}`

  return (
    <div className={`practice-plan-item${obj.done ? ' practice-plan-item--done' : ''}`}>
      {!obj.done && obj.detailWhy ? (
        <ObjectiveWhyToggle
          id={detailId}
          detail={obj.detailWhy}
          ariaLabel="Pourquoi cet objectif ?"
        >
          <p className="practice-plan-item-title">{obj.title}</p>
        </ObjectiveWhyToggle>
      ) : (
        <p className="practice-plan-item-title">{obj.title}</p>
      )}
      {!obj.done && obj.description ? (
        <p className="practice-plan-item-desc">{obj.description}</p>
      ) : null}
      <div className="practice-plan-bar-wrap" aria-hidden="true">
        <div className="practice-plan-bar" style={{ width: `${pct}%` }} />
      </div>
      <p className={`practice-plan-progress-text ${obj.done ? 'practice-plan-done' : ''}`}>
        {obj.done ? 'Objectif atteint' : `${obj.current} / ${obj.target}`}
      </p>
      {!obj.done && obj.successRateMeta ? (
        <p className="practice-plan-meta">{obj.successRateMeta}</p>
      ) : null}
      <div className="practice-plan-actions">
        {obj.type === 'node' && onGoParcours ? (
          <button
            type="button"
            className="practice-plan-btn practice-plan-btn--primary"
            onClick={onGoParcours}
            aria-label={`Ouvrir le parcours pour ${obj.title}`}
          >
            Ouvrir le parcours
          </button>
        ) : null}
        {obj.type === 'skill' && onWorkSkill && obj.focus ? (
          <button
            type="button"
            className="practice-plan-btn practice-plan-btn--primary"
            onClick={() => onWorkSkill(obj.focus)}
            aria-label={`Exercices ciblés sur ${practiceName}`}
          >
            Exercices ciblés
          </button>
        ) : null}
        {obj.codex && onOpenCodex ? (
          <CodexLinkButton
            ariaLabel={`Ouvrir la fiche Codex : ${obj.title}`}
            onClick={() => onOpenCodex(obj.codex.ficheId, obj.codex.sectionId)}
          />
        ) : null}
      </div>
    </div>
  )
}

/**
 * Objectifs pédagogiques hebdomadaires (client, localStorage hebdo) — pas de reward XP.
 */
function PracticePlan({
  profileStats,
  parcoursSummary,
  attempts,
  onGoParcours,
  onWorkSkill,
  onOpenCodex,
  onAllComplete,
  availableTags = null
}) {
  const weekKey = getWeekKey()

  const objectives = useMemo(() => {
    const list = []
    const stored = loadWeekState()
    const useStored = stored?.weekKey === weekKey

    if (parcoursSummary?.activeNodeId && !parcoursSummary.isMastered) {
      const target = 3
      const current = Math.min(target, parcoursSummary.consecutiveSuccesses ?? 0)
      const nodeCodex = getCodexLinkForNode(parcoursSummary.activeNodeId)
      list.push({
        id: `node_${parcoursSummary.activeNodeId}`,
        title: `Maîtriser « ${parcoursSummary.activeNodeLabel} »`,
        description: buildNodeObjectiveCopy(target),
        detailWhy: null,
        successRateMeta: null,
        current,
        target,
        type: 'node',
        done: isNodeMastered({
          consecutiveSuccesses: parcoursSummary.consecutiveSuccesses,
          successes: parcoursSummary.successes,
          averageScore: parcoursSummary.averageScore
        }) || current >= target,
        codex: nodeCodex
      })
    }

    const weak = getActionableWeakPoints(profileStats || {}, attempts || [], availableTags, {
      max: 2
    })
    const skillFocusList = useStored?.skillFocus
      ? weak.filter(
          (w) =>
            !(
              w.type === useStored.skillFocus.type && w.value === useStored.skillFocus.value
            )
        )
      : weak
    const orderedSkills = useStored?.skillFocus
      ? [
          weak.find(
            (w) => w.type === useStored.skillFocus.type && w.value === useStored.skillFocus.value
          ),
          ...skillFocusList
        ].filter(Boolean)
      : weak

    orderedSkills.slice(0, 2).forEach((focus) => {
      if (!focus) return
      const target = 3
      const relatedOk = countWeeklyTargetedSuccesses(attempts || [], focus)
      const current = Math.min(target, relatedOk)
      const done = (focus.successRate ?? 0) >= 70 || relatedOk >= target
      const practiceName = focus.practiceLabel || focus.label
      const codex = getCodexForWeakPoint(focus)
      const copy = buildSkillObjectiveCopy(focus, target)
      list.push({
        id: `skill_${focus.type}_${focus.value}`,
        title: formatSkillTitle(practiceName),
        description: copy.description,
        detailWhy: copy.detailWhy,
        successRateMeta: copy.successRateMeta,
        current: done ? target : current,
        target,
        type: 'skill',
        focus,
        done,
        codex
      })
    })

    return applyWeeklyLocks(list, stored, weekKey)
  }, [profileStats, parcoursSummary, attempts, weekKey, availableTags])

  const allDone = objectives.length > 0 && objectives.every((o) => o.done)

  useEffect(() => {
    const skillObj = objectives.find((o) => o.type === 'skill' && o.focus && !o.locked)
    const prev = loadWeekState()
    const prevLocked =
      prev?.weekKey === weekKey && prev.lockedObjectives ? { ...prev.lockedObjectives } : {}
    objectives.forEach((obj) => {
      if (obj.done) {
        prevLocked[obj.id] = snapshotObjective(obj)
      }
    })
    saveWeekState({
      weekKey,
      skillFocus: skillObj?.focus
        ? { type: skillObj.focus.type, value: skillObj.focus.value }
        : prev?.weekKey === weekKey
          ? prev.skillFocus
          : null,
      lockedObjectives: prevLocked
    })
    onAllComplete?.(allDone)
  }, [objectives, weekKey, onAllComplete, allDone])

  if (!objectives.length) {
    return (
      <div className="practice-plan" aria-labelledby={SECTION_TITLE_ID}>
        <h3 id={SECTION_TITLE_ID} className="practice-plan-title">
          Objectifs de la semaine
        </h3>
        <p className="practice-plan-subtitle">Basés sur ton parcours et tes points faibles</p>
        <p className="practice-plan-empty">
          Pas d’objectif précis pour l’instant. Continue le parcours ou le mode libre pour générer
          tes objectifs de la semaine.
        </p>
      </div>
    )
  }

  return (
    <div className="practice-plan" aria-labelledby={SECTION_TITLE_ID}>
      <h3 id={SECTION_TITLE_ID} className="practice-plan-title">
        Objectifs de la semaine
      </h3>
      <p className="practice-plan-subtitle">Basés sur ton parcours et tes points faibles</p>
      {allDone ? (
        <>
          <p className="practice-plan-celebration" role="status">
            Bravo — tous tes objectifs de la semaine sont atteints.
          </p>
          <p className="practice-plan-renewal">Nouveaux objectifs lundi.</p>
        </>
      ) : null}
      {objectives.map((obj) => (
        <PracticePlanObjectiveCard
          key={obj.id}
          obj={obj}
          onGoParcours={onGoParcours}
          onWorkSkill={onWorkSkill}
          onOpenCodex={onOpenCodex}
        />
      ))}
    </div>
  )
}

export default PracticePlan
